import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { journeyAPI, personaAPI } from '../../services/api'
import PersonaSection from './PersonaSection'
import EpisodeCard from './EpisodeCard'
import SpeakerLegend from './SpeakerLegend'
import MultiJudgePanel from './MultiJudgePanel'
import ReasoningTracesPanel from './ReasoningTracesPanel'

export default function JourneyViewer() {
  const { journeyId } = useParams()
  const navigate = useNavigate()
  const [journey, setJourney] = useState(null)
  const [persona, setPersona] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [allExpanded, setAllExpanded] = useState(false)
  const [judgeRefreshKey, setJudgeRefreshKey] = useState(0)

  useEffect(() => {
    if (!journeyId) return
    setLoading(true)
    journeyAPI.get(journeyId)
      .then(data => {
        setJourney(data)
        // Fetch the full persona for detailed profile & archetype
        if (data.persona_id) {
          personaAPI.get(data.persona_id)
            .then(setPersona)
            .catch(() => {}) // non-critical
        }
      })
      .catch(() => setError('Failed to load journey'))
      .finally(() => setLoading(false))
  }, [journeyId])

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    )
  }

  if (error || !journey) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <div className="alert alert-error max-w-md">
          <span>{error || 'Journey not found'}</span>
        </div>
      </div>
    )
  }

  const totalTurns = journey.episodes?.reduce(
    (sum, ep) => sum + (ep.dialogue?.length || 0), 0
  ) || 0
  const nurseCount = journey.episodes?.filter(ep => ep.nurse_present).length || 0

  return (
    <div className="min-h-screen bg-base-200">
      {/* Header */}
      <div className="navbar bg-base-100 shadow-sm px-6">
        <div className="flex-1 gap-2">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/generator')}>
            ← Generator
          </button>
          <span className="text-lg font-bold">Journey Viewer</span>
        </div>
        <div className="flex-none gap-2">
          <button
            className="btn btn-outline btn-sm"
            onClick={() => journeyAPI.exportJson(journey.id)}
          >
            Export Journey
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">
        {/* Journey header */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title text-2xl">{journey.persona_name}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
              <div className="bg-base-200 rounded-lg p-3 text-center">
                <div className="text-sm text-base-content/60">Patient</div>
                <div className="font-semibold">{journey.patient_name}</div>
              </div>
              <div className="bg-base-200 rounded-lg p-3 text-center">
                <div className="text-sm text-base-content/60">Episodes</div>
                <div className="font-semibold">{journey.total_episodes}</div>
              </div>
              <div className="bg-base-200 rounded-lg p-3 text-center">
                <div className="text-sm text-base-content/60">Dialogue Turns</div>
                <div className="font-semibold">{totalTurns}</div>
              </div>
              <div className="bg-base-200 rounded-lg p-3 text-center">
                <div className="text-sm text-base-content/60">Nurse Present</div>
                <div className="font-semibold">{nurseCount}/{journey.total_episodes}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Persona section */}
        <PersonaSection journey={journey} persona={persona} />

        {/* Legend + expand/collapse */}
        <div className="flex items-center justify-between">
          <SpeakerLegend />
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setAllExpanded(!allExpanded)}
          >
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </button>
        </div>

        {/* Episodes */}
        {journey.episodes?.map(episode => (
          <EpisodeCard
            key={episode.episode_number}
            episode={episode}
            forceExpanded={allExpanded}
          />
        ))}

        {/* Multi-Model Judge Comparison */}
        <MultiJudgePanel journeyId={journeyId} onEvaluationComplete={() => setJudgeRefreshKey(k => k + 1)} />

        {/* Reasoning Traces */}
        <ReasoningTracesPanel journey={journey} journeyId={journeyId} refreshKey={judgeRefreshKey} />
      </div>
    </div>
  )
}
